import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import React, { ComponentType, useEffect } from "react";
import { jwtDecode } from 'jwt-decode';
import { PageLoader } from "../../app/page-loader";
import { useAppDispatch } from "../../app/hooks";
import { fetchUser, selectFetchUserStatus, selectUser } from "../user/user-slice";
import { selectAccessToken, setAccessToken, setAuthServerUser } from "./authentication-slice";
import { useSelector } from "react-redux";

interface AuthenticationGuardProps {
  component: ComponentType;
}

const AuthenticationGuard: React.FC<AuthenticationGuardProps> = ({
  component,
}) => {
  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently, user, logout } = useAuth0();

  // React useEffect: applies after rendering
  useEffect(() => {
    const execute = async () => {
      const accessToken = await getAccessTokenSilently();
      const decoded = jwtDecode(accessToken);
      if (decoded.exp) {
        const expiration = new Date(decoded.exp * 1000);
        if (expiration < new Date()) {
          localStorage.clear();
          logout();
        } else {
          dispatch(setAccessToken(accessToken));
          dispatch(setAuthServerUser(user));
        }
      }
    }
    execute();
  });

  const accessToken = useSelector(selectAccessToken);
  const appUser = useSelector(selectUser);
  const fetchUserStatus = useSelector(selectFetchUserStatus);

  /** Fetch user and keep on loading while it is not retrieved. */
  useEffect(() => {
    if (accessToken && fetchUserStatus === 'idle') {
      dispatch(fetchUser(accessToken));
    }
  }, [dispatch, accessToken, fetchUserStatus])

  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => (
      <PageLoader />
    ),
  });

  const Loader = withAuthenticationRequired(PageLoader, {
    onRedirecting: () => (
      <PageLoader />
    ),
  });

  return (
    <>
      {appUser && <Component />}
      {(!appUser) && <Loader />}
    </>
  );
};

export default AuthenticationGuard;